import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  CheckCircle2,
  Package,
  FileText,
  Monitor,
  TrendingUp,
  Tag,
  Activity,
  Wrench,
  DollarSign,
  BarChart3,
  Layers,
  Settings,
  ChevronDown,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Separator } from '../../ui/separator';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import blank from '../../../assets/blank.png';

interface IMobileNavProps {
  pathname: string;
  user: {
    name: string;
    email: string;
    avatar: boolean;
    avatarURL: string;
  };
  onNavigate: () => void;
}

const getInitials = (name?: string): string => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const MobileNav: React.FC<IMobileNavProps> = ({
  pathname,
  user,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const [productsOpen, setProductsOpen] = useState(
    pathname.includes('/products'),
  );
  const [reportsOpen, setReportsOpen] = useState(pathname.includes('/reports'));
  const [configsOpen, setConfigsOpen] = useState(
    pathname.includes('/configurations'),
  );

  const isActive = (path: string) => {
    if (path === '/') {
      return pathname === path;
    }
    return pathname.startsWith(path);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage
              src={user.avatar ? user.avatarURL : blank}
              alt={user.name || 'Usuário'}
            />
            <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          </div>
        </div>
      </div>

      <Separator />

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          <Button
            variant={isActive('/') ? 'secondary' : 'ghost'}
            className="w-full justify-start gap-3"
            onClick={() => handleNavigate('/')}
          >
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </Button>

          <Button
            variant={isActive('/users') ? 'secondary' : 'ghost'}
            className="w-full justify-start gap-3"
            onClick={() => handleNavigate('/users')}
          >
            <Users className="h-4 w-4" />
            Usuários
          </Button>

          <Button
            variant={isActive('/companies') ? 'secondary' : 'ghost'}
            className="w-full justify-start gap-3"
            onClick={() => handleNavigate('/companies')}
          >
            <Building2 className="h-4 w-4" />
            Companhias
          </Button>

          <Button
            variant={isActive('/admin/plan-approvals') ? 'secondary' : 'ghost'}
            className="w-full justify-start gap-3"
            onClick={() => handleNavigate('/admin/plan-approvals')}
          >
            <CheckCircle2 className="h-4 w-4" />
            Aprovações
          </Button>

          <Separator className="my-2" />

          <Collapsible open={productsOpen} onOpenChange={setProductsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant={pathname.includes('/products') ? 'secondary' : 'ghost'}
                className="w-full justify-between"
              >
                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4" />
                  Produtos
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    productsOpen && 'rotate-180',
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 pl-4 mt-1">
              <Button
                variant={
                  isActive('/products/softwares') ? 'secondary' : 'ghost'
                }
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/products/softwares')}
              >
                <Monitor className="h-3.5 w-3.5" />
                Softwares
              </Button>
              <Button
                variant={isActive('/products/plans') ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/products/plans')}
              >
                <TrendingUp className="h-3.5 w-3.5" />
                Planos
              </Button>
              <Button
                variant={isActive('/products/offers') ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/products/offers')}
              >
                <Tag className="h-3.5 w-3.5" />
                Ofertas
              </Button>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible open={reportsOpen} onOpenChange={setReportsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant={pathname.includes('/reports') ? 'secondary' : 'ghost'}
                className="w-full justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4" />
                  Relatórios
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    reportsOpen && 'rotate-180',
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 pl-4 mt-1">
              <Button
                variant={
                  isActive('/reports/subscriptions') ? 'secondary' : 'ghost'
                }
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/reports/subscriptions')}
              >
                <Activity className="h-3.5 w-3.5" />
                Assinaturas
              </Button>
              <Button
                variant={isActive('/reports/actions') ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/reports/actions')}
              >
                <Wrench className="h-3.5 w-3.5" />
                Ações
              </Button>
              <Button
                variant={
                  isActive('/reports/sales-comission') ? 'secondary' : 'ghost'
                }
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/reports/sales-comission')}
              >
                <DollarSign className="h-3.5 w-3.5" />
                Vendas
              </Button>
            </CollapsibleContent>
          </Collapsible>

          <Separator className="my-2" />

          <Collapsible open={configsOpen} onOpenChange={setConfigsOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant={
                  pathname.includes('/configurations') ? 'secondary' : 'ghost'
                }
                className="w-full justify-between"
              >
                <div className="flex items-center gap-3">
                  <Settings className="h-4 w-4" />
                  Configurações
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform',
                    configsOpen && 'rotate-180',
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 pl-4 mt-1">
              <Button
                variant={
                  isActive('/configurations/levels') ? 'secondary' : 'ghost'
                }
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/configurations/levels')}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Níveis
              </Button>
              <Button
                variant={
                  isActive('/configurations/categories') ? 'secondary' : 'ghost'
                }
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/configurations/categories')}
              >
                <Layers className="h-3.5 w-3.5" />
                Categorias
              </Button>
              <Button
                variant={
                  isActive('/configurations/options') ? 'secondary' : 'ghost'
                }
                className="w-full justify-start gap-3"
                size="sm"
                onClick={() => handleNavigate('/configurations/options')}
              >
                <Settings className="h-3.5 w-3.5" />
                Opções
              </Button>
            </CollapsibleContent>
          </Collapsible>
        </nav>
      </ScrollArea>

      <Separator />

      <div className="p-3 space-y-1">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3"
          onClick={() => handleNavigate('/profile')}
        >
          <UserIcon className="h-4 w-4" />
          Minha Conta
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-destructive hover:text-destructive"
          onClick={() => handleNavigate('/logout')}
        >
          <LogOut className="h-4 w-4" />
          Sair
        </Button>
      </div>
    </div>
  );
};

export default MobileNav;
